/*
 * Order keys (CONTRACT §5.3). The server's keys.py and this module implement
 * exactly the same normaliser, so "Basic Metabolic Panel (BMP)" from
 * LiveScribe, "BMP" from a Follow up row and "BMP (repeat potassium)" all key
 * to lk:249 with the practice's aliases (meta.order_aliases), and BMP is never
 * offered or ordered twice.
 *
 * Pure: no React, no network.
 */

// norm(name): NFKC -> lowercase -> drop every "(...)" -> runs of non [a-z0-9]
// to one space -> trim -> drop a leading "order " -> collapse spaces.
export const normOrder = (s) =>
  String(s || "")
    .normalize("NFKC")
    .toLowerCase()
    .replace(/\([^)]*\)/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/^order\s+/, "")
    .replace(/\s+/g, " ");

// orderKey(name, aliases): "" -> null; aliases[n] ("lk:<id>") when present,
// else "txt:" + n with spaces as "-". Digits are kept (a dose matters).
export const orderKey = (name, aliases = {}) => {
  const n = normOrder(name);
  if (!n) return null;
  const a = aliases && Object.prototype.hasOwnProperty.call(aliases, n) ? aliases[n] : null;
  return a || "txt:" + n.replace(/ /g, "-");
};

/*
 * Client-side aliases, used only when the server sent none (the legacy
 * kg/todo fallback). They key synonyms together with "alias:" ids that can
 * never collide with the server's "lk:" ids.
 */
export const FALLBACK_ORDER_ALIASES = (() => {
  const groups = {
    bmp: ["bmp", "basic metabolic panel", "chem 7", "chem7"],
    cmp: ["cmp", "comprehensive metabolic panel"],
    cbc: ["cbc", "complete blood count", "cbc with differential", "cbc w diff"],
    a1c: ["a1c", "hba1c", "hemoglobin a1c", "glycated hemoglobin"],
    lipid: ["lipid panel", "lipid profile", "lipids", "fasting lipid panel"],
    tsh: ["tsh", "thyroid stimulating hormone"],
    uacr: ["uacr", "urine albumin creatinine ratio", "microalbumin creatinine ratio"],
    psa: ["psa", "prostate specific antigen"],
    inr: ["pt inr", "pt ptt inr", "inr", "prothrombin time"],
    mg: ["magnesium", "mg"],
    phos: ["phosphorus", "phosphate"],
  };
  const out = {};
  Object.entries(groups).forEach(([k, names]) => names.forEach((n) => { out[n] = `alias:${k}`; }));
  return out;
})();

/*
 * Analyte -> the panel a "repeat" re-draws (CONTRACT §3.3 row actions). The
 * server sends the row's action with the practice's catalogue name; this map
 * is for the legacy fallback and for tests.
 */
export const REPEAT_PANEL = {
  potassium: "BMP", k: "BMP", sodium: "BMP", na: "BMP", co2: "BMP", bicarbonate: "BMP",
  glucose: "BMP", calcium: "BMP", ca: "BMP", bun: "BMP", "urea nitrogen": "BMP", creatinine: "BMP",
  hemoglobin: "CBC", hgb: "CBC", platelets: "CBC", "platelet count": "CBC", wbc: "CBC", "white blood cell count": "CBC",
  inr: "PT/PTT/INR", "pt inr": "PT/PTT/INR",
  magnesium: "Magnesium", mg: "Magnesium",
  phosphorus: "Phosphorus", phosphate: "Phosphorus",
  a1c: "HbA1c", hba1c: "HbA1c", "hemoglobin a1c": "HbA1c",
  tsh: "TSH",
  ldl: "Lipid panel", "ldl cholesterol": "Lipid panel", cholesterol: "Lipid panel", triglycerides: "Lipid panel", hdl: "Lipid panel",
};

// The panel to repeat for a test name ("Potassium" -> "BMP"), or null.
export const repeatPanelFor = (testName) => {
  const n = normOrder(testName);
  if (!n) return null;
  if (REPEAT_PANEL[n]) return REPEAT_PANEL[n];
  const first = n.split(" ")[0];
  return REPEAT_PANEL[first] || null;
};

/*
 * Is this order already on the visit? Compares keys, never raw names:
 *   - Current Orders (orders/get for the chart's order date),
 *   - every existing_order the GET reported,
 *   - the orders added from this tab in this session.
 * `name` may be a name or an already-computed key ("lk:249").
 */
export const isAddedOrder = (nameOrKey, { aliases = {}, currentOrders = [], extraKeys = [] } = {}) => {
  const key = /^(lk|txt|alias):/.test(String(nameOrKey || "")) ? nameOrKey : orderKey(nameOrKey, aliases);
  if (!key) return false;
  for (const k of extraKeys) if (k === key) return true;
  return currentOrders.some((o) => orderKey(o?.ordername || o?.description || o?.name || "", aliases) === key);
};

/*
 * AI order_type ("lab", "prescription", ...) -> the practice's ordertype
 * strings, with the name heuristic for an empty/unknown type. Copied from the
 * legacy tab (CareIQTab.jsx mapOrderType, unchanged) so both tabs file a
 * LiveScribe suggestion under the same order type.
 */
export const mapOrderType = (type, orderName = "") => {
  const normalised = String(type || "").toLowerCase().trim();
  const map = {
    lab: "Labs", labs: "Labs",
    imaging: "Diagnostic Tests", "diagnostic test": "Diagnostic Tests", "diagnostic tests": "Diagnostic Tests",
    procedure: "Procedures/Tests", procedures: "Procedures/Tests", "procedures/tests": "Procedures/Tests",
    referral: "Referrals", referrals: "Referrals",
    prescription: "Prescriptions", prescriptions: "Prescriptions", rx: "Prescriptions", medication: "Prescriptions",
    medications: "Prescriptions", med: "Prescriptions", meds: "Prescriptions", drug: "Prescriptions", drugs: "Prescriptions",
    antibiotic: "Prescriptions", antibiotics: "Prescriptions", antiviral: "Prescriptions", statin: "Prescriptions",
    injection: "Injections", injections: "Injections", shot: "Injections",
    vitamin: "Vitamin", vitamins: "Vitamin", supplement: "Vitamin", supplements: "Vitamin",
  };
  if (map[normalised]) return map[normalised];
  const name = String(orderName || "").toLowerCase();
  const drugLikeKeywords = [
    "antibiotic", "antiviral", "antifungal", "statin", "ssri", "snri", "nsaid", "steroid", "prescription", "syrup",
    "tablet", "capsule", "ointment", "cream", "inhaler", "drops", "amoxicillin", "azithromycin", "augmentin",
    "ibuprofen", "acetaminophen", "tylenol", "advil", "metformin", "atorvastatin", "lisinopril", "amlodipine",
    "losartan", "sertraline", "omeprazole", "pantoprazole", "albuterol", "prednisone", "gabapentin",
  ];
  if (drugLikeKeywords.some((k) => name.includes(k))) return "Prescriptions";
  if (["vitamin", "vit b", "vit d", "biotin", "folate", "iron", "magnesium"].some((k) => name.includes(k))) return "Vitamin";
  if (["panel", "lipid", "cbc", "cmp", "bmp", "a1c", "tsh", "psa", "ua", "urinalysis"].some((k) => name.includes(k))) return "Labs";
  if (["x-ray", "xray", "ultrasound", "ct ", "mri", "echo", "ekg", "ecg", "stress test", "pft", "sonogram"].some((k) => name.includes(k))) return "Diagnostic Tests";
  if (["referral", "refer to", "consult"].some((k) => name.includes(k))) return "Referrals";
  if (["injection", "shot", "inj "].some((k) => name.includes(k))) return "Injections";
  return "Procedures/Tests";
};
